import { FC, ReactNode, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import AutoRefundErrorImage from 'features/avia-auto-refund/assets/auto-refund-error.webp'
import AutoRefundInfoImage from 'features/avia-auto-refund/assets/auto-refund-info.webp'
import AutoRefundSuccessImage from 'features/avia-auto-refund/assets/auto-refund-success.webp'
import {
  getAutoRefundAmounts,
  isAutoRefundCheckData,
  isAutoRefundProcessSuccessful,
} from 'features/avia-auto-refund/lib/auto-refund'

import { AutoRefundApi, AutoRefundCheckData } from 'shared/api/avia/auto-refund'
import { WHATSAPP_NUMBER } from 'shared/constants'
import { openExternalUrl } from 'shared/lib/external-open'
import { priceShow } from 'shared/lib/priceShow'
import { Button } from 'shared/ui/button'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'

import { AutoRefundConfirmation } from '../auto-refund-confirmation'
import { AutoRefundDialog } from '../auto-refund-dialog'

import styles from './styles.module.scss'

interface Props {
  ticketId: string
  price: number
  priceWithPromoCode?: string
  onRefundSuccess: () => Promise<void>
}

export const AutoRefundAction: FC<Props> = ({
  ticketId,
  price,
  priceWithPromoCode,
  onRefundSuccess,
}) => {
  const { t } = useTranslation()
  const { closeDialog, openDialog } = useDialogBottomSheet()

  const checkingRef = useRef(false)
  const mountedRef = useRef(true)
  const popupOpenRef = useRef(false)
  const [checking, setChecking] = useState(false)
  const [completed, setCompleted] = useState(false)

  const openPopup = (child: ReactNode) => {
    popupOpenRef.current = true
    openDialog({
      child,
      isDialog: false,
      isClickable: false,
      borderRadius: 20,
      padding: [0],
    })
  }

  const closePopup = () => {
    popupOpenRef.current = false
    closeDialog()
  }

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false

      if (popupOpenRef.current) {
        popupOpenRef.current = false
        closeDialog()
      }
    }
  }, [closeDialog])

  const showUnavailable = () => {
    openPopup(
      <AutoRefundDialog
        image={AutoRefundErrorImage}
        title={t('avia_auto_refund.unavailable.title')}
        description={t('avia_auto_refund.unavailable.description')}
        button={t('avia_auto_refund.unavailable.close')}
        additionalButton={t('write_whatsapp')}
        onClick={closePopup}
        onClickAdditional={() =>
          openExternalUrl(`https://wa.me/${WHATSAPP_NUMBER}`)
        }
      />,
    )
  }

  const showSuccess = () => {
    openPopup(
      <AutoRefundDialog
        image={AutoRefundSuccessImage}
        title={t('avia_auto_refund.success.title')}
        description={t('avia_auto_refund.success.description')}
        button={t('avia_auto_refund.success.close')}
        onClick={() => {
          closePopup()
          void onRefundSuccess()
        }}
      />,
    )
  }

  const processAutoRefund = async (checkData: AutoRefundCheckData) => {
    try {
      const { data } = await AutoRefundApi.processAutoRefund({
        id: ticketId,
        confirm_code: checkData.confirm_code,
        refund_fee: checkData.payment_fee.total,
      })

      if (!mountedRef.current) return

      if (!isAutoRefundProcessSuccessful(data.data)) {
        showUnavailable()
        return
      }

      setCompleted(true)
      showSuccess()
    } catch {
      if (mountedRef.current) showUnavailable()
    }
  }

  const showConfirmation = (
    checkData: AutoRefundCheckData,
    refundAmount: number,
  ) => {
    openPopup(
      <AutoRefundConfirmation
        refundAmount={priceShow(String(refundAmount))}
        onConfirm={() => processAutoRefund(checkData)}
      />,
    )
  }

  const showSummary = (checkData: AutoRefundCheckData) => {
    const amounts = getAutoRefundAmounts(
      { price, price_with_promo_code: priceWithPromoCode },
      checkData.payment_fee.total,
    )

    if (!amounts) {
      showUnavailable()
      return
    }

    openPopup(
      <AutoRefundDialog
        image={AutoRefundInfoImage}
        title={t('avia_auto_refund.title')}
        description={
          <div className={styles.amounts}>
            <span>
              {t('avia_auto_refund.total', {
                amount: priceShow(String(amounts.total)),
              })}
            </span>
            <span>
              {t('avia_auto_refund.fee', {
                amount: priceShow(String(amounts.fee)),
              })}
            </span>
            <span>
              {t('avia_auto_refund.refund', {
                amount: priceShow(String(amounts.refund)),
              })}
            </span>
          </div>
        }
        button={t('avia_auto_refund.agree')}
        onClick={() => showConfirmation(checkData, amounts.refund)}
      />,
    )
  }

  const checkAutoRefund = async () => {
    if (checkingRef.current) return

    checkingRef.current = true
    setChecking(true)

    try {
      const { data } = await AutoRefundApi.checkAutoRefund({ id: ticketId })

      if (!mountedRef.current) return

      if (!isAutoRefundCheckData(data.data)) {
        showUnavailable()
        return
      }

      showSummary(data.data)
    } catch {
      if (mountedRef.current) showUnavailable()
    } finally {
      checkingRef.current = false

      if (mountedRef.current) setChecking(false)
    }
  }

  if (completed) return null

  return (
    <div className={styles.action}>
      <Button
        variant="transparent"
        loading={checking}
        onClick={() => void checkAutoRefund()}
      >
        <span className="medium-14">{t('avia_auto_refund.action')}</span>
      </Button>
    </div>
  )
}
